import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-lg">
        <p className="font-heading text-sm mb-2 text-slate-800 font-semibold">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const WeeklyComparisonChart = ({ 
  data, 
  title,
  yAxisLabel = "",
  formatValue = (v) => v.toLocaleString()
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="chart-container p-6"
      data-testid={`chart-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="mb-6">
        <h3 className="font-heading text-lg font-semibold text-slate-800">
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          First 7 Days vs Last 7 Days
        </p>
      </div>
      
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="metric" 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
              tickFormatter={formatValue}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px', fontFamily: 'DM Sans', fontSize: '12px' }}
            />
            <Bar 
              dataKey="First 7 Days" 
              fill="#94A3B8" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            />
            <Bar 
              dataKey="Last 7 Days" 
              fill="#3B82F6" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export const GrowthComparisonChart = ({ 
  data, 
  title,
  showGrowthColors = true
}) => {
  const dataWithGrowth = data.map(item => ({
    ...item,
    growth: item["Last 7 Days"] - item["First 7 Days"],
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="chart-container p-6"
      data-testid={`growth-chart-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="mb-6">
        <h3 className="font-heading text-lg font-semibold text-slate-800">
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Growth Analysis
        </p>
      </div>
      
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={dataWithGrowth}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="metric" 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              tick={{ fill: '#64748B', fontSize: 12, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={{ stroke: '#E2E8F0' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px', fontFamily: 'DM Sans', fontSize: '12px' }}
            />
            <Bar 
              dataKey="First 7 Days" 
              fill="#94A3B8" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            />
            <Bar 
              dataKey="Last 7 Days" 
              radius={[4, 4, 0, 0]}
              animationDuration={1000}
            >
              {dataWithGrowth.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={showGrowthColors && entry.growth >= 0 ? '#059669' : '#DC2626'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default WeeklyComparisonChart;
